import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, PencilSimple, Trash, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function FailureReasons() {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [reasons, setReasons] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ name: '' });

  const fetchReasons = async () => {
    try {
      const res = await axios.get(`${API}/failure-reasons`, { withCredentials: true });
      setReasons(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchReasons(); }, []);

  const openAdd = () => { setEditing(null); setForm({ name: '' }); setDialogOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name }); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      if (editing) {
        await axios.put(`${API}/failure-reasons/${editing.reason_id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/failure-reasons`, form, { withCredentials: true });
      }
      toast.success(t('savedSuccess'));
      setDialogOpen(false);
      fetchReasons();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/failure-reasons/${deleting.reason_id}`, { withCredentials: true });
      toast.success(t('deletedSuccess'));
      setDeleteOpen(false);
      fetchReasons();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  return (
    <Layout>
      <div data-testid="failure-reasons-page">
        <button
          onClick={() => navigate('/building-settings')}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4"
          data-testid="failure-reasons-back-to-settings"
        >
          <ArrowLeft size={14} />
          <span className="font-semibold">{t('settings') || 'Settings'}</span>
        </button>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('failureReasons')}</h1>
          {isAdmin && (
            <Button onClick={openAdd} className="bg-zinc-950 rounded-none" data-testid="add-failure-reason-btn">
              <Plus size={18} className="mr-2" />{t('addFailureReason')}
            </Button>
          )}
        </div>

        {reasons.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500" data-testid="no-failure-reasons">{t('noFailureReasons')}</div>
        ) : (
          <div className="bg-white border border-zinc-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="failure-reasons-table">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('reasonName')}</th>
                  {isAdmin && <th className="text-right px-5 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider w-24">{t('actions')}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {reasons.map(r => (
                  <tr key={r.reason_id} className="hover:bg-zinc-50 transition-colors" data-testid={`reason-row-${r.reason_id}`}>
                    <td className="px-5 py-3 font-medium">{r.name}</td>
                    {isAdmin && (
                      <td className="px-5 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-zinc-100 transition-colors" data-testid={`edit-reason-${r.reason_id}`}>
                            <PencilSimple size={16} />
                          </button>
                          <button onClick={() => { setDeleting(r); setDeleteOpen(true); }} className="p-1.5 hover:bg-red-50 text-red-600 transition-colors" data-testid={`delete-reason-${r.reason_id}`}>
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{editing ? t('editFailureReason') : t('addFailureReason')}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>{t('reasonName')} *</Label><Input value={form.name} onChange={e => setForm({ name: e.target.value })} className="mt-1 rounded-none" required data-testid="reason-name-input" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-reason-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>{t('deleteFailureReason')}</AlertDialogTitle><AlertDialogDescription>{t('confirmDeleteFailureReason')}</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 rounded-none" data-testid="confirm-delete-reason">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
